/**
 * Feedback Environment Capture
 *
 * Utilities for capturing browser environment information
 * for feedback submissions and auto-error detection.
 */

import type { FeedbackEnvironment } from '@/types/feedback';

/**
 * Detect browser name from user agent
 */
function detectBrowser(ua: string): string {
  if (ua.includes('Firefox')) {
return 'Firefox';
}
  if (ua.includes('Edg')) {
return 'Edge';
}
  if (ua.includes('Chrome')) {
return 'Chrome';
}
  if (ua.includes('Safari')) {
return 'Safari';
}
  if (ua.includes('Opera') || ua.includes('OPR')) {
return 'Opera';
}
  return 'Unknown';
}

/**
 * Detect browser version from user agent
 */
function detectBrowserVersion(ua: string): string {
  let match: RegExpMatchArray | null;

  if (ua.includes('Firefox')) {
    match = ua.match(/Firefox\/(\d+(\.\d+)?)/);
  } else if (ua.includes('Edg')) {
    match = ua.match(/Edg\/(\d+(\.\d+)?)/);
  } else if (ua.includes('Chrome')) {
    match = ua.match(/Chrome\/(\d+(\.\d+)?)/);
  } else if (ua.includes('Safari')) {
    match = ua.match(/Version\/(\d+(\.\d+)?)/);
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    match = ua.match(/(?:Opera|OPR)\/(\d+(\.\d+)?)/);
  } else {
    match = null;
  }

  return match ? match[1] : 'Unknown';
}

/**
 * Detect operating system from user agent
 */
function detectOS(ua: string): string {
  if (ua.includes('Windows NT 10')) {
return 'Windows 10/11';
}
  if (ua.includes('Windows NT 6.3')) {
return 'Windows 8.1';
}
  if (ua.includes('Windows NT 6.2')) {
return 'Windows 8';
}
  if (ua.includes('Windows NT 6.1')) {
return 'Windows 7';
}
  if (ua.includes('Windows')) {
return 'Windows';
}
  if (ua.includes('Mac OS X')) {
return 'macOS';
}
  if (ua.includes('Linux')) {
return 'Linux';
}
  if (ua.includes('Android')) {
return 'Android';
}
  if (ua.includes('iPhone') || ua.includes('iPad')) {
return 'iOS';
}
  return 'Unknown';
}

/**
 * Capture current browser environment
 */
export function captureEnvironment(): FeedbackEnvironment {
  if (typeof window === 'undefined') {
    // Server-side rendering
    return {};
  }

  const ua = navigator.userAgent;

  return {
    browser: detectBrowser(ua),
    browser_version: detectBrowserVersion(ua),
    os: detectOS(ua),
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    page_url: window.location.pathname,
    page_title: document.title,
    user_agent: ua,
    timestamp: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
}

/**
 * Format environment for display
 */
export function formatEnvironment(env: FeedbackEnvironment): string {
  const parts: string[] = [];

  if (env.browser && env.browser_version) {
    parts.push(`${env.browser} ${env.browser_version}`);
  } else if (env.browser) {
    parts.push(env.browser);
  }

  if (env.os) {
    parts.push(env.os);
  }

  if (env.viewport_width && env.viewport_height) {
    parts.push(`${env.viewport_width}x${env.viewport_height}`);
  }

  if (env.page_url) {
    parts.push(env.page_url);
  }

  return parts.join(' • ');
}

/**
 * Get environment summary for display in forms
 */
export function getEnvironmentSummary(): string {
  const env = captureEnvironment();
  return formatEnvironment(env);
}
